import { HistoryIcon } from '@atgfr8/icons';
import {
  clearToastActivity,
  dismissToastActivity,
  markToastActivitySeen,
  toastActivityEntries,
} from '@components/Toast/toastActivity';
import { useNavigate } from '@solidjs/router';
import {
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from 'solid-js';
import { Portal } from 'solid-js/web';

const accentClass = (type: string) => {
  switch (type) {
    case 'Success':
      return 'border-l-announcement-feature';
    case 'Caution':
      return 'border-l-caution-yellow';
    case 'Error':
      return 'border-l-error-red';
    default:
      return 'border-l-armstrong-blue';
  }
};

const entryHeading = (title: string | undefined, type: string) => {
  if (title && title !== 'Info') {
    return title;
  }
  if (type === 'Info') {
    return undefined;
  }
  return type;
};

const openActivityLink = (
  event: MouseEvent,
  href: string,
  onOpen: (href: string) => void,
) => {
  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }
  event.preventDefault();
  onOpen(href);
};

const ActivityMessage = (props: {
  message: string;
  href?: string;
  linkText?: string;
  onOpen: (href: string) => void;
}) => {
  const inlineNumber = () => {
    const href = props.href;
    const linkText = props.linkText;
    if (!href || !linkText) {
      return undefined;
    }
    const index = props.message.lastIndexOf(linkText);
    if (index < 0) {
      return undefined;
    }
    const numeric = /^\d+$/.test(linkText);
    const before = index > 0 ? props.message[index - 1] : '';
    const after = props.message[index + linkText.length] ?? '';
    if (numeric) {
      if (/\d/.test(before) || /\d/.test(after)) {
        return undefined;
      }
    } else if (before !== '#') {
      return undefined;
    }
    return {
      before: props.message.slice(0, index),
      after: props.message.slice(index + linkText.length),
      href,
      linkText,
    };
  };

  const linkHref = () => {
    const href = props.href;
    const linkText = props.linkText;
    if (!href || !linkText || inlineNumber()) {
      return undefined;
    }
    return href;
  };

  const messageHref = () => {
    const href = props.href;
    if (!href || props.linkText) {
      return undefined;
    }
    return href;
  };

  return (
    <Show
      when={inlineNumber()}
      fallback={
        <Show
          when={messageHref()}
          fallback={
            <>
              {props.message}
              <Show when={linkHref()}>
                {(href) => (
                  <a
                    href={href()}
                    class="mt-1 block w-fit cursor-pointer text-armstrong-blue hover:underline"
                    onClick={(event) =>
                      openActivityLink(event, href(), props.onOpen)
                    }
                  >
                    {props.linkText}
                  </a>
                )}
              </Show>
            </>
          }
        >
          {(href) => (
            <a
              href={href()}
              class="cursor-pointer text-armstrong-blue hover:underline"
              onClick={(event) => openActivityLink(event, href(), props.onOpen)}
            >
              {props.message}
            </a>
          )}
        </Show>
      }
    >
      {(parts) => (
        <>
          {parts().before}
          <a
            href={parts().href}
            class="cursor-pointer text-armstrong-blue hover:underline"
            onClick={(event) =>
              openActivityLink(event, parts().href, props.onOpen)
            }
          >
            {parts().linkText}
          </a>
          {parts().after}
        </>
      )}
    </Show>
  );
};

export default function ToastActivityBell() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = createSignal<HTMLButtonElement>();
  const [panelEl, setPanelEl] = createSignal<HTMLDivElement>();
  const [open, setOpen] = createSignal(false);
  const [arrivalMessage, setArrivalMessage] = createSignal('');
  const initialEntries = toastActivityEntries();
  let announcedEntryId = initialEntries.length > 0 ? initialEntries[0].id : 0;
  const [panelStyle, setPanelStyle] = createSignal({
    top: '0px',
    right: '0px',
  });

  const placePanel = () => {
    const anchor = anchorEl();
    if (!anchor) {
      return;
    }
    const rect = anchor.getBoundingClientRect();
    setPanelStyle({
      top: `${rect.bottom + 8}px`,
      right: `${window.innerWidth - rect.right}px`,
    });
  };

  const toggle = () => {
    setOpen((current) => !current);
    markToastActivitySeen();
  };

  const focusPanel = () => {
    panelEl()?.focus({ preventScroll: true });
  };

  onMount(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!open()) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (anchorEl()?.contains(target) || panelEl()?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !open()) {
        return;
      }
      setOpen(false);
      anchorEl()?.focus({ preventScroll: true });
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    });
  });

  createEffect(() => {
    const panel = panelEl();
    if (!open() || panel === undefined) {
      return;
    }
    queueMicrotask(() => {
      if (panel.isConnected) {
        panel.focus({ preventScroll: true });
      }
    });
  });

  createEffect(() => {
    const entries = toastActivityEntries();
    if (entries.length === 0) {
      return;
    }
    const newest = entries[0];
    if (newest.id <= announcedEntryId) {
      return;
    }
    announcedEntryId = newest.id;
    const heading = entryHeading(newest.title, newest.type);
    const linkText = newest.linkText;
    const message =
      linkText && !newest.message.includes(linkText)
        ? `${newest.message} ${linkText}`
        : newest.message;
    const text = heading ? `${heading}. ${message}` : message;
    setArrivalMessage('');
    queueMicrotask(() => setArrivalMessage(text));
  });

  createEffect(() => {
    if (!open()) {
      return;
    }
    placePanel();
    const onMove = () => placePanel();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    onCleanup(() => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    });
  });

  return (
    <>
      <div class="sr-only" aria-live="polite" aria-atomic="true">
        {arrivalMessage()}
      </div>
      <button
        type="button"
        class="mr-1 flex h-10 cursor-pointer items-center px-2 text-white"
        title="Recent activity"
        ref={setAnchorEl}
        aria-haspopup="dialog"
        aria-expanded={open()}
        aria-label="Recent activity"
        data-testid="toast-activity-clock"
        onClick={toggle}
      >
        <HistoryIcon class="text-white" />
      </button>
      <Show when={open()}>
        <Portal>
          <div
            ref={setPanelEl}
            class="fixed z-[1500] flex max-h-96 w-96 cursor-default flex-col overflow-hidden bg-white text-left shadow-md"
            style={panelStyle()}
            role="dialog"
            tabIndex={-1}
            aria-label="Recent activity"
          >
            <div class="flex shrink-0 items-center justify-between border-b border-grayish px-4 py-2">
              <span class="text-sm font-semibold text-armstrong-primary">
                Recent activity
              </span>
              <Show when={toastActivityEntries().length > 0}>
                <button
                  type="button"
                  class="cursor-pointer text-armstrong-xs text-armstrong-blue hover:underline"
                  onClick={() => {
                    focusPanel();
                    clearToastActivity();
                  }}
                  data-testid="toast-activity-clear-button"
                >
                  Clear
                </button>
              </Show>
            </div>
            <div class="min-h-0 flex-1 overflow-auto">
              <Show
                when={toastActivityEntries().length > 0}
                fallback={
                  <p class="px-4 py-6 text-center text-armstrong-xs text-gray-500">
                    No activity this session.
                  </p>
                }
              >
                <ul>
                  <For each={toastActivityEntries()}>
                    {(entry) => (
                      <li class="border-b border-grayish">
                        <div
                          class={`relative border-l-4 px-4 py-2 ${accentClass(entry.type)}`}
                        >
                          <button
                            type="button"
                            class="absolute top-2 right-4 cursor-pointer text-armstrong-xs text-gray-500 hover:text-armstrong-primary"
                            aria-label={`Dismiss ${entryHeading(entry.title, entry.type) ?? entry.message}`}
                            onClick={() => {
                              focusPanel();
                              dismissToastActivity(entry.id);
                            }}
                          >
                            Dismiss
                          </button>
                          <div class="pr-16">
                            <Show when={entryHeading(entry.title, entry.type)}>
                              <span class="block text-armstrong-xs font-semibold text-armstrong-primary">
                                {entryHeading(entry.title, entry.type)}
                              </span>
                            </Show>
                            <p
                              class={`wrap-break-word whitespace-pre-line text-armstrong-sm ${entryHeading(entry.title, entry.type) ? 'mt-1' : ''}`}
                            >
                              <ActivityMessage
                                message={entry.message}
                                href={entry.href}
                                linkText={entry.linkText}
                                onOpen={(href) => {
                                  navigate(href);
                                  setOpen(false);
                                }}
                              />
                            </p>
                          </div>
                        </div>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  );
}
